import { t } from "@lingui/macro";
import {
  useRoute,
  RouteProp,
  useNavigation,
  NavigationProp,
} from "@react-navigation/native";
import graphql from "babel-plugin-relay/macro";
import { useAtom } from "jotai";
import { Suspense, useEffect, useCallback, useState } from "react";
import { ScrollView } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import {
  PreloadedQuery,
  useFragment,
  usePreloadedQuery,
  useQueryLoader,
} from "react-relay";

import { BookingInfo, BookingPolicy } from "@/features/BookingParts";
import { ClosedRequestWindowBanner } from "@/screens/BookingDetail/components/ClosedRequestWindowBanner";
import { useFlags } from "@/utils/launchDarkly/useFlags";
import { useToast } from "@whoosh/design-system/src/components/overlay/Toast/useToast";
import { Box, HStack } from "@whoosh/design-system/src/components/primitive";

import { NotificationsNudgeModal } from "../../../../../features/Session/components/NotificationsNudgeModal/NotificationsNudgeModal";
import { useCurrentUserHasSeenNotificationNudgeModal } from "../../../../../features/Session/hooks/useCurrentUserHasSeenNotificationNudgeModal";
import { ClubStackNavigatorParamList } from "../../../../../navigators/ClubNavigator";
import { HomeTabsNavigatorParamList } from "../../../../../navigators/HomeTabsNavigator/types";
import { BookingConfirmedModal } from "../BookingConfirmedModal/BookingConfirmedModal";
import { BookingSummaryMultipleViewQuery } from "./__generated__/BookingSummaryMultipleViewQuery.graphql";
import { BookingSummaryMultipleViewSession_data$key } from "./__generated__/BookingSummaryMultipleViewSession_data.graphql";
import { BookingSummaryMultipleView_booking$key } from "./__generated__/BookingSummaryMultipleView_booking.graphql";
import { BookingType } from "./__generated__/BookingSummaryQuery.graphql";
import { useInitializeBookingSummaryAnalyticsDataAtom } from "./atoms/analytics/useInitializeBookingSummaryAnalyticsDataAtom";
import {
  isConfirmedBookingModalOpenAtom,
  isNotificationsNudgeModalOpenAtom,
} from "./atoms/stores";
import {
  BOOKING_SUMMARY_CONTENT_MAX_WIDTH,
  useIsBookingSummaryNarrow,
  useIsBookingSummaryStacked,
} from "./bookingSummaryWidth";
import { BookingDetailsView } from "./components/BookingDetailsView/BookingDetailsView";
import { BookingHeader } from "./components/BookingHeader/BookingHeader";
import { BookingPayment } from "./components/BookingPayment/BookingPayment";
import {
  useIsRequestConfigRequestWindowClosed,
  useShouldPresentBookingAsLockedRequest,
} from "./components/BookingRequest/components/AvailableTeeTimes/hooks/useShouldPresentBookingAsLockedRequest";
import { RequestInfoBanner } from "./components/BookingRequest/components/RequestInfoBanner/RequestInfoBanner";
import { BookingReservation } from "./components/BookingReservation/BookingReservation";
import { BookingSummaryLayout } from "./components/BookingSummaryLayout/BookingSummaryLayout";
import { BookingSummarySkeleton } from "./components/BookingSummarySkeleton/BookingSummarySkeleton";
import { ChargesSummary } from "./components/ChargesSummary/ChargesSummary";
import { EmptyGroupWarningModal } from "./components/EmptyGroupWarningModal/EmptyGroupWarningModal";
import { InviteOthersBox } from "./components/InviteOthersBox/InviteOthersBox";
import { MemberNote } from "./components/MemberNote/MemberNote";
import { PaymentCardEntryModals } from "./components/PaymentCardEntryModals/PaymentCardEntryModals";
import { RemoteLockCode } from "./components/RemoteLockCode/RemoteLockCode";

type BookingSummaryMultipleViewProps = {
  currentSessionMemberShipTypeId: string;
  currentSessionUserClientId: string;
  sessionFragmentRef: BookingSummaryMultipleViewSession_data$key;
  supportsPrivateBookings: boolean;
  isLessonBooking: boolean;
};

export const BookingSummaryMultipleView = ({
  currentSessionMemberShipTypeId,
  currentSessionUserClientId,
  sessionFragmentRef,
  supportsPrivateBookings,
  isLessonBooking,
}: BookingSummaryMultipleViewProps) => {
  const route =
    useRoute<RouteProp<ClubStackNavigatorParamList, "BookingSummary">>();

  const { slotIds } = route.params.params as {
    slotIds: string[];
  };
  const { slotOrBookingId } = route.params;
  const { patronLoyalty } = useFlags();

  const [queryRef, loadQuery] = useQueryLoader<BookingSummaryMultipleViewQuery>(
    BookingSummaryMultipleViewQueryString
  );

  useEffect(() => {
    if (route.params.slotOrBookingId === "multiple-tee-times") {
      const bookingType: BookingType = slotOrBookingId.includes("LessonBooking")
        ? "LESSON"
        : "STANDARD";
      loadQuery(
        {
          timeSlotIds: slotIds,
          bookingType,
          membershipTypeId: currentSessionMemberShipTypeId,
          patronLoyalty: !!patronLoyalty,
        },
        { fetchPolicy: "store-and-network" }
      );
    }
  }, [
    loadQuery,
    route.params.slotOrBookingId,
    slotIds,
    currentSessionMemberShipTypeId,
    patronLoyalty,
    slotOrBookingId,
  ]);

  return (
    <Suspense
      fallback={
        <BookingSummarySkeleton
          supportsPrivateBookings={supportsPrivateBookings}
          isLessonBooking={isLessonBooking}
          clubSupportsPrivateBookings={supportsPrivateBookings}
          isMultipleBooking={true}
        />
      }
    >
      {queryRef && currentSessionMemberShipTypeId && sessionFragmentRef ? (
        <BookingSummaryMultipleViewQueryRenderer
          currentSessionMemberShipTypeId={currentSessionMemberShipTypeId}
          currentSessionUserClientId={currentSessionUserClientId}
          sessionFragmentRef={sessionFragmentRef}
          bookingSummaryMultipleViewQueryRef={queryRef}
        />
      ) : null}
    </Suspense>
  );
};

type BookingSummaryMultipleViewQueryRendererProps = {
  currentSessionMemberShipTypeId: string;
  currentSessionUserClientId: string;
  sessionFragmentRef: BookingSummaryMultipleViewSession_data$key;
  bookingSummaryMultipleViewQueryRef: PreloadedQuery<BookingSummaryMultipleViewQuery>;
};

export const BookingSummaryMultipleViewQueryRenderer = ({
  bookingSummaryMultipleViewQueryRef,
  currentSessionMemberShipTypeId,
  currentSessionUserClientId,
  sessionFragmentRef,
}: BookingSummaryMultipleViewQueryRendererProps) => {
  const data = usePreloadedQuery(
    BookingSummaryMultipleViewQueryString,
    bookingSummaryMultipleViewQueryRef
  );
  return (
    <BookingSummaryMultipleViewQueryRendererContent
      data={data}
      currentSessionMemberShipTypeId={currentSessionMemberShipTypeId}
      currentSessionUserClientId={currentSessionUserClientId}
      sessionFragmentRef={sessionFragmentRef}
    />
  );
};

type BookingSummaryMultipleViewQueryRendererContentProps = Omit<
  BookingSummaryMultipleViewQueryRendererProps,
  "bookingSummaryMultipleViewQueryRef"
> & {
  data: BookingSummaryMultipleViewQuery["response"];
};

const BookingSummaryMultipleViewQueryRendererContent = ({
  data,
  currentSessionMemberShipTypeId,
  currentSessionUserClientId,
  sessionFragmentRef,
}: BookingSummaryMultipleViewQueryRendererContentProps) => {
  const navigation =
    useNavigation<NavigationProp<HomeTabsNavigatorParamList>>();

  const [isEmptyGroupWarningModalOpen, setIsEmptyGroupWarningModalOpen] =
    useState(false);
  const [isConfirmedBookingModalOpen, setIsConfirmedBookingModalOpen] = useAtom(
    isConfirmedBookingModalOpenAtom
  );
  const [pendingBookingConfirm, setPendingBookingConfirm] = useState<
    (() => void) | null
  >(null);

  const isNarrow = useIsBookingSummaryNarrow();
  const isStacked = useIsBookingSummaryStacked();

  const dataSession = useFragment(
    BookingSummaryMultipleViewSessionQueryString,
    sessionFragmentRef
  );

  // Flatten bookings across all provided timeSlots
  const isNonNullish = <T,>(value: T | null | undefined): value is T =>
    value !== null && value !== undefined;
  const bookings =
    data.timeSlots?.flatMap((timeSlot) =>
      (timeSlot?.bookings?.edges ?? [])
        .map((edge) => edge?.node)
        .filter(isNonNullish)
    ) ?? [];
  const firstBooking = useFragment<BookingSummaryMultipleView_booking$key>(
    BookingSummaryMultipleViewBookingFragmentString,
    bookings[0] ?? null
  );

  const handleGoBack = useCallback(() => {
    if (navigation.canGoBack()) {
      navigation.goBack();
    } else {
      navigation.navigate("MainHome");
    }
  }, [navigation]);

  const bookingIds = bookings.map((booking) => booking.id);

  const isBookingOwner =
    currentSessionUserClientId ===
    (bookings[bookings.length - 1]?.ownerClient?.id ?? null);

  const clubSupportsPrivateBookings =
    bookings[bookings.length - 1]?.startSlot?.agenda?.facility?.club
      ?.supportsPrivate;

  const isLessonBooking =
    bookings[bookings.length - 1]?.__typename === "LessonBooking";

  const numberOfCreatedBookings = bookings.filter(
    (booking) => booking && booking.rounds && booking.rounds.count >= 1
  ).length;

  const hasBookingWithNoRounds = bookings.some(
    (booking) => (booking?.rounds?.count ?? 0) === 0
  );

  const hasPublicSeenNotificationNudgeModal =
    useCurrentUserHasSeenNotificationNudgeModal(dataSession);

  const shouldShowBookingReservation =
    !isStacked && isLessonBooking
      ? null
      : isStacked || clubSupportsPrivateBookings;

  const handleRequestBookingConfirmation = useCallback(
    (proceed: () => void) => {
      if (hasBookingWithNoRounds) {
        setPendingBookingConfirm(() => proceed);
        setIsEmptyGroupWarningModalOpen(true);
        return;
      }

      proceed();
    },
    [hasBookingWithNoRounds]
  );

  const handleCloseEmptyGroupWarningModal = useCallback(
    (shouldConfirm?: boolean) => {
      if (shouldConfirm && pendingBookingConfirm) {
        pendingBookingConfirm();
      }

      setPendingBookingConfirm(null);
      setIsEmptyGroupWarningModalOpen(false);
    },
    [pendingBookingConfirm]
  );

  if (!bookings || bookings.length === 0) {
    return null;
  }

  return (
    <>
      <Box width="100%" backgroundColor="surface-secondary">
        <BookingHeader
          clubRef={dataSession?.currentClientProfile?.member?.club ?? null}
          bookingRef={bookings[0] ?? null}
          handleGoBack={handleGoBack}
          isMultipleBooking={true}
        />
      </Box>
      <ScrollView
        showsVerticalScrollIndicator={false}
        bounces={false}
        contentContainerStyle={{ flexGrow: 1 }}
        style={{ flex: 1 }}
      >
        <Box
          gap={isNarrow ? 2 : 0}
          backgroundColor="surface-secondary"
          style={{
            paddingBottom: 124,
          }}
        >
          {bookings.map((booking) => (
            <BookingSummaryMultipleViewInner
              key={booking.id}
              currentServerTime={data.currentServerTime}
              currentSessionMemberShipTypeId={currentSessionMemberShipTypeId}
              currentSessionUserClientId={currentSessionUserClientId}
              sessionFragmentRef={sessionFragmentRef}
              bookingRef={booking}
              numBookings={bookings.length}
            />
          ))}
        </Box>
      </ScrollView>
      {isConfirmedBookingModalOpen && firstBooking ? (
        <BookingConfirmedModal
          isOpen={isConfirmedBookingModalOpen}
          setConfirmedBookingModal={setIsConfirmedBookingModalOpen}
          bookingRef={firstBooking}
        />
      ) : null}
      {shouldShowBookingReservation ? (
        <BookingReservation
          bookingFragmentRef={bookings[bookings.length - 1]}
          currentServerTime={data.currentServerTime}
          handleGoBack={handleGoBack}
          isMemberCaller={
            dataSession?.currentClientProfile?.member?.__typename === "Member"
          }
          membershipTypeId={currentSessionMemberShipTypeId}
          hasPublicSeenNotificationNudgeModal={
            hasPublicSeenNotificationNudgeModal
          }
          isMultipleBooking={true}
          bookingIds={bookingIds}
          onRequestConfirm={handleRequestBookingConfirmation}
          isBookingOwner={isBookingOwner}
        />
      ) : null}
      <EmptyGroupWarningModal
        isOpen={isEmptyGroupWarningModalOpen}
        onClose={handleCloseEmptyGroupWarningModal}
        numberOfCreatedBookings={numberOfCreatedBookings}
        totalNumberOfBookings={bookings.length}
      />
    </>
  );
};

type BookingSummaryMultipleViewInnerProps = {
  currentServerTime?: string | null;
  currentSessionMemberShipTypeId: string;
  currentSessionUserClientId: string;
  sessionFragmentRef: BookingSummaryMultipleViewSession_data$key;
  bookingRef: BookingSummaryMultipleView_booking$key;
  numBookings: number;
};

export const BookingSummaryMultipleViewInner = ({
  currentServerTime,
  currentSessionMemberShipTypeId,
  currentSessionUserClientId,
  sessionFragmentRef,
  bookingRef,
  numBookings,
}: BookingSummaryMultipleViewInnerProps) => {
  const isNarrow = useIsBookingSummaryNarrow();
  const isStacked = useIsBookingSummaryStacked();
  const insets = useSafeAreaInsets();
  const data = useFragment(
    BookingSummaryMultipleViewSessionQueryString,
    sessionFragmentRef
  );
  const booking = useFragment<BookingSummaryMultipleView_booking$key>(
    BookingSummaryMultipleViewBookingFragmentString,
    bookingRef
  );

  useInitializeBookingSummaryAnalyticsDataAtom(booking);
  const hasPublicSeenNotificationNudgeModal =
    useCurrentUserHasSeenNotificationNudgeModal(data);
  const clubSlug = data?.currentClientProfile?.member?.club?.slug ?? null;

  const currentUserClientId = currentSessionUserClientId;
  const ownerId = booking?.ownerClient?.id ?? null;
  const isBookingOwner =
    currentUserClientId && ownerId && currentUserClientId === ownerId;

  const [isNotificationsNudgeModalOpen, setIsNotificationsNudgeModalOpen] =
    useAtom(isNotificationsNudgeModalOpenAtom);

  const clubSupportsPrivateBookings =
    booking?.startSlot?.agenda?.facility?.club?.supportsPrivate;

  const navigation =
    useNavigation<NavigationProp<HomeTabsNavigatorParamList>>();

  const shouldShowChargesSummary = isStacked && !clubSupportsPrivateBookings;

  const shouldShowBookingPayment = !isStacked && !clubSupportsPrivateBookings;
  const shouldPresentAsRequest = useShouldPresentBookingAsLockedRequest(
    booking ?? null
  );
  const isClosedRequestWindow = useIsRequestConfigRequestWindowClosed(
    booking ?? null
  );

  const handleGoBack = useCallback(() => {
    if (navigation.canGoBack()) {
      navigation.goBack();
    } else {
      navigation.navigate("MainHome");
    }
  }, [navigation]);

  const { showToast } = useToast();

  //If we get no booking -- we just bail and go back.
  useEffect(() => {
    if (!booking) {
      showToast({
        title: t`Booking not found`,
        description: t`Sorry about that but I couldn't find that booking.`,
        semanticStyle: "error",
        toastId: "booking-not-found-error-toast",
      });
      handleGoBack();
    }
  }, [booking, handleGoBack, showToast]);

  return (
    <HStack
      width="100%"
      style={{
        paddingTop: insets.top,
        paddingBottom: insets.bottom,
      }}
      flex={numBookings > 1 ? undefined : 1}
      marginBottom={numBookings > 1 ? 1 : 0}
      backgroundColor="surface-secondary"
    >
      {!isStacked ? (
        <Box
          backgroundColor={
            clubSupportsPrivateBookings
              ? "surface-secondary"
              : "surface-primary"
          }
          flexGrow={1}
          flexShrink={1}
          flexBasis={0}
        />
      ) : null}
      <Box
        backgroundColor={
          clubSupportsPrivateBookings ? "surface-secondary" : "surface-primary"
        }
        flexShrink={1}
        flexGrow={1}
      >
        <Box
          maxWidth={BOOKING_SUMMARY_CONTENT_MAX_WIDTH}
          marginLeft="auto"
          width="100%"
          marginTop={isNarrow ? 0 : 3}
          backgroundColor={"surface-primary"}
          marginRight={shouldShowBookingPayment ? 3 : "auto"}
        >
          <BookingSummaryLayout
            isMultipleBookings={numBookings > 1}
            header={<></>}
            body={
              <>
                <BookingInfo
                  bookingRef={booking}
                  forceRequestState={shouldPresentAsRequest}
                  hideStartTime={shouldPresentAsRequest}
                >
                  <>
                    {isClosedRequestWindow ? (
                      <ClosedRequestWindowBanner
                        clubPhoneNumber={
                          booking?.startSlot?.agenda?.facility?.club
                            ?.phoneNumber?.formattedNumber ?? undefined
                        }
                      />
                    ) : (
                      <RequestInfoBanner bookingRef={booking} />
                    )}
                    <RemoteLockCode
                      bookingRef={booking}
                      fragmentRef={booking?.startSlot?.agenda?.facility ?? null}
                    />
                    <MemberNote bookingRef={booking} />
                  </>
                </BookingInfo>

                <InviteOthersBox bookingRef={booking} clubSlug={clubSlug} />
                <BookingDetailsView
                  bookingRef={booking}
                  currentSessionMemberShipTypeId={
                    currentSessionMemberShipTypeId
                  }
                  currentSessionUserClientId={currentSessionUserClientId}
                />
                {shouldShowChargesSummary ? (
                  <ChargesSummary
                    bookingRef={booking}
                    isBookingOwner={!!isBookingOwner}
                    showLoyaltyEnrollment={false}
                  />
                ) : null}
                <BookingPolicy bookingRef={booking} />
              </>
            }
            footer={<></>}
          />
        </Box>
      </Box>

      {shouldShowBookingPayment ? (
        <BookingPayment
          handleGoBack={handleGoBack}
          bookingFragmentRef={booking}
          isMemberCaller={
            data?.currentClientProfile?.member?.__typename === "Member"
          }
          currentServerTime={currentServerTime}
          membershipTypeId={currentSessionMemberShipTypeId}
          hasPublicSeenNotificationNudgeModal={
            hasPublicSeenNotificationNudgeModal
          }
          isBookingOwner={!!isBookingOwner}
          showLoyaltyEnrollment={false}
        />
      ) : null}
      {!isStacked ? (
        <Box
          backgroundColor={"surface-secondary"}
          flexGrow={1}
          flexShrink={1}
          flexBasis={0}
        />
      ) : null}
      {data && booking ? <PaymentCardEntryModals bookingRef={booking} /> : null}

      {hasPublicSeenNotificationNudgeModal ? null : (
        <NotificationsNudgeModal
          userId={data?.user?.id ?? ""}
          isOpen={isNotificationsNudgeModalOpen}
          setNudgeModal={setIsNotificationsNudgeModalOpen}
          isNewFlow={true}
        />
      )}
    </HStack>
  );
};

const BookingSummaryMultipleViewSessionQueryString = graphql`
  fragment BookingSummaryMultipleViewSession_data on Session {
    ...useCurrentUserHasSeenNotificationNudgeModal_session
    user {
      id
    }
    currentClientProfile {
      ... on MemberProfile {
        member {
          __typename
          club {
            slug
            ...BookingHeaderFragment_BookingSummary
          }
          membershipType {
            id
          }
          ... on Member {
            id
          }
          ... on Customer {
            id
          }
        }
      }
    }
  }
`;

const BookingSummaryMultipleViewBookingFragmentString = graphql`
  fragment BookingSummaryMultipleView_booking on Booking
  @argumentDefinitions(
    patronLoyalty: { type: "Boolean!", defaultValue: false }
  ) {
    id
    __typename
    ...useGetBookingAnalyticsMetadata_booking
    ...RemoteLockCode_Booking
    ...ChargesSummary_booking @arguments(patronLoyalty: $patronLoyalty)
    ...PaymentCardEntryModals_booking
    ...BookingPayment_Booking @arguments(patronLoyalty: $patronLoyalty)
    ...InviteOthersBox_Booking
    ...BookingInfo_Booking
    ...BookingDetailsView_Booking
    ...BookingPolicy_Booking
    ...BookingPaymentMethodSelect_booking
    ...BookingConfirmedModal_Booking
    ...BookingReservation_Booking @arguments(patronLoyalty: $patronLoyalty)
    ...BookingHeaderFragment_booking
    ...MemberNote_Booking
    ...BookingDetailsView_Booking
    ...RequestInfoBanner_booking
    ...useShouldPresentBookingAsLockedRequest_booking
    ... on StandardBooking {
      status
      restrictionStatuses {
        groupStatuses {
          statuses {
            restriction {
              name
            }
          }
        }
        roundStatuses {
          statuses {
            restriction {
              name
            }
          }
        }
      }
    }
    ... on LessonBooking {
      status
      restrictionStatuses {
        groupStatuses {
          statuses {
            restriction {
              name
            }
          }
        }
        roundStatuses {
          statuses {
            restriction {
              name
            }
          }
        }
      }
    }
    ownerClient {
      ... on Customer {
        id
      }
      ... on Member {
        id
      }
    }
    startSlot {
      id
      agenda {
        facility {
          club {
            supportsPrivate
            phoneNumber {
              formattedNumber
            }
          }
          ...RemoteLockCode_BookingSummaryRemoteLockCodeConfig
        }
      }
    }
  }
`;

export const BookingSummaryMultipleViewQueryString = graphql`
  query BookingSummaryMultipleViewQuery(
    $timeSlotIds: [ID!]
    $bookingType: BookingType!
    $membershipTypeId: ID!
    $patronLoyalty: Boolean!
  ) {
    currentServerTime
    timeSlots(ids: $timeSlotIds) {
      id
      bookings {
        edges {
          node {
            rounds {
              count
            }
            __typename
            id
            ownerClient {
              ... on Customer {
                id
              }
              ... on Member {
                id
              }
            }
            ...BookingHeaderFragment_booking
            ...BookingReservation_Booking
              @arguments(patronLoyalty: $patronLoyalty)
            ...BookingSummaryMultipleView_booking
              @arguments(patronLoyalty: $patronLoyalty)
            startSlot {
              agenda {
                date
                facility {
                  club {
                    supportsPrivate
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;
